// Live messages written into the query cache (WEB-F-RND-04), so every screen reading the REST results updates
// without a refetch: the latest values (POST /v1/latest) and the device's state (GET /v1/devices/{id} and the
// device lists), alarms (the Alarms screen and a device page's alarm list) and automations (their pages and scenes).
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { components } from "../api/generated/vision-api";
import { applyAlarm, type Alarm } from "../features/alarms/alarms";
import type { AlarmMessage, AutomationMessage, DeviceStateMessage, LatestMessage } from "./socket";

type LatestResult = components["schemas"]["LatestResult"];
type Device = components["schemas"]["Device"];
type DeviceList = components["schemas"]["DevicePage"];
type AlarmPage = components["schemas"]["AlarmPage"];
type AutomationItem = components["schemas"]["AutomationItem"];
type SceneItem = { name: string; enabled: boolean; last_ran_at: string | null };

export function patchLatest(qc: QueryClient, orgId: string, m: LatestMessage): void {
  qc.setQueriesData<LatestResult>({ queryKey: ["org", orgId, "/v1/latest"] }, (old) => {
    if (!old) return old;
    let points: LatestResult["points"] | undefined;
    for (const [k, v] of Object.entries(m.values)) {
      const key = `${m.device}/${k}`;
      if (!(key in old.points)) continue;
      // A value backfilled from the device's log may be older than the one shown; keep the newer.
      const shown = old.points[key];
      if (shown && Date.parse(shown.at) > Date.parse(v.at)) continue;
      points ??= { ...old.points };
      points[key] = { value: v.value as never, at: v.at };
    }
    return points ? { ...old, points } : old;
  });
}

export function patchDeviceState(qc: QueryClient, orgId: string, m: DeviceStateMessage): void {
  const seen = m.last_seen_at !== undefined ? { last_seen_at: m.last_seen_at } : {};
  // A moving device's new position (live 0.2.0) moves its marker wherever it is drawn.
  const moved = m.position ? { position: m.position } : {};
  qc.setQueriesData<LatestResult>({ queryKey: ["org", orgId, "/v1/latest"] }, (old) => {
    const d = old?.devices[m.device];
    if (!old || !d) return old;
    // The widgets know three states; a device never seen counts as offline there.
    const state = m.status === "never_seen" ? "offline" : m.status;
    return { ...old, devices: { ...old.devices, [m.device]: { ...d, state, ...seen, ...moved } } };
  });
  qc.setQueryData<Device>(["org", orgId, "/v1/devices/{device_id}", m.device], (old) =>
    old ? { ...old, status: m.status, ...seen, ...moved } : old,
  );
  // The device lists too: the Map screen draws its markers from them (WEB-F-DEV-05).
  qc.setQueriesData<InfiniteData<DeviceList>>({ queryKey: ["org", orgId, "/v1/devices"] }, (old) => {
    if (!old?.pages.some((p) => p.items.some((d) => d.id === m.device))) return old;
    return {
      ...old,
      pages: old.pages.map((p) => ({
        ...p,
        items: p.items.map((d) => (d.id === m.device ? { ...d, status: m.status, ...seen, ...moved } : d)),
      })),
    };
  });
}

/**
 * An alarm raised, acknowledged or cleared (live 0.3.0): the Alarms screen, the navigation's count and a device page's
 * alarm list move at once instead of at their next refresh (WEB-ALM-05).
 */
export function patchAlarm(qc: QueryClient, orgId: string, m: AlarmMessage): void {
  const cleared = m.event === "cleared";
  qc.setQueryData<Alarm[]>(["org", orgId, "/v1/alarms", "all"], (old) =>
    old ? applyAlarm(old, m.alarm, cleared) : old,
  );
  qc.setQueryData<AlarmPage>(["org", orgId, "/v1/alarms", "device", m.alarm.device], (old) =>
    old ? { ...old, items: applyAlarm(old.items, m.alarm, cleared) } : old,
  );
}

const RESULTS = ["running", "ok", "failed", "skipped"] as const;
const PAUSES = ["plan_limit", "owner_lost_access", "loop", "device_gone"] as const;
const oneOf = <T extends string>(list: readonly T[], v: unknown): T | null =>
  (list as readonly unknown[]).includes(v) ? (v as T) : null;

/**
 * A server automation ran, failed or was paused (live 0.4.0, specs/web-automations WAU-11): its row on the
 * Automations page, its detail, the device pages' sections and the scene buttons change in place, and its run log is
 * read again.
 */
export function patchAutomation(qc: QueryClient, orgId: string, m: AutomationMessage): void {
  const a = m.automation;
  const result = oneOf(RESULTS, a.last_result);
  const next: Partial<AutomationItem> = {
    name: a.name,
    enabled: a.enabled,
    last_ran_at: a.last_ran_at,
    ...("last_result" in a ? { last_result: result === "running" ? null : result } : {}),
    ...("paused_reason" in a ? { paused_reason: oneOf(PAUSES, a.paused_reason) } : {}),
  };
  const apply = (items: AutomationItem[] | undefined) =>
    items?.some((i) => i.id === a.id) ? items.map((i) => (i.id === a.id ? { ...i, ...next } : i)) : items;
  qc.setQueryData<AutomationItem[]>(["org", orgId, "/v1/automations", "all"], apply);
  qc.setQueriesData<AutomationItem[]>({ queryKey: ["org", orgId, "/v1/devices/{device_id}/automations"] }, apply);
  qc.setQueryData<AutomationItem>(["org", orgId, "/v1/automations/{automation_id}", a.id], (old) =>
    old ? { ...old, ...next } : old,
  );
  qc.setQueryData<Record<string, SceneItem>>(["org", orgId, "/v1/automations", "manual"], (old) =>
    old?.[a.id] ? { ...old, [a.id]: { name: a.name, enabled: a.enabled, last_ran_at: a.last_ran_at } } : old,
  );
  if (m.event !== "paused")
    void qc.invalidateQueries({ queryKey: ["org", orgId, "/v1/automations/{automation_id}/runs", a.id] });
}
